<template>
  <SlideOver :open="on" @close="close">
    <div class="w-screen max-w-md h-full">
      <form
        @submit.prevent="storeRole"
        class="h-full divide-y divide-gray-200 flex flex-col bg-white shadow-xl"
      >
        <div class="flex-1 h-0 overflow-y-auto">
          <div class="py-6 px-4 bg-light-blue-700 sm:px-6">
            <div class="flex items-center justify-between">
              <h2
                id="slide-over-heading"
                class="text-lg font-medium text-white"
              >
                {{ t("role_create") }}
              </h2>
              <div class="ml-3 h-7 flex items-center">
                <button
                  type="button"
                  @click="close()"
                  class="
                    bg-light-blue-700
                    rounded-md
                    focus:outline-none focus:ring-2 focus:ring-white
                  "
                >
                  <XIcon class="h-6 w-6 hover:text-white text-light-blue-200" />
                </button>
              </div>
            </div>
            <div class="mt-1">
              <p class="text-sm text-light-blue-300">
                {{ t("description") }}
              </p>
            </div>
          </div>
          <div class="flex-1 flex flex-col justify-between">
            <div class="px-4 divide-y divide-gray-200 sm:px-6">
              <div class="space-y-6 pt-6 pb-5">
                <ui-input
                  ref="name"
                  :invalid="'name' in form.errors"
                  :error="form.errors.name"
                  type="text"
                  :label="t('name')"
                  v-model="form.name"
                ></ui-input>

                <div>
                  <h3 class="text-sm font-medium text-gray-700 mb-4">
                    {{ t("permissions") }}
                  </h3>
                  <div
                    v-show="'permissions' in form.errors"
                    class="
                      rounded
                      p-3
                      bg-red-100
                      flex
                      text-red-700
                      items-center
                      mb-3
                    "
                  >
                    <ExclamationIcon class="w-5 h-5 mr-2 text-red-700" />
                    <p>{{ form.errors.permissions }}</p>
                  </div>
                  <div class="flex items-center justify-between flex-wrap">
                    <ui-toggle
                      v-for="permission in selectedPermissions"
                      :key="permission.permission"
                      v-model="permission.value"
                      :label="permission.display_name"
                      class="pr-4 mb-4"
                    ></ui-toggle>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="flex-shrink-0 px-4 py-4 flex justify-end">
          <ui-button
            backgroundStyling="bg-white"
            borderStyling="border border-gray-300"
            textColorStyling="text-gray-700 hover:text-gray-500"
            type="button"
            @click="close"
            >{{ t("cancel") }}</ui-button
          >

          <ui-button :disabled="form.processing" type="submit" class="ml-4">
            {{ t("save") }}
          </ui-button>
        </div>
      </form>
    </div>
  </SlideOver>
</template>

<script>
import { XIcon, ExclamationIcon } from "@heroicons/vue/solid";
import { useI18n } from "vue-i18n";
import SlideOver from "@/Shared/SlideOver";

export default {
  emits: ["toggleVisibility"],

  components: {
    XIcon,
    ExclamationIcon,
    SlideOver,
  },
  props: {
    on: { type: Boolean, required: true },

    permissions: { type: Object, required: false },
  },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  computed: {
    activatedPermissions() {
      return this.selectedPermissions
        .filter((permission) => permission.value)
        .map((permission) => permission.permission);
    },
  },

  data() {
    return {
      form: this.$inertia.form(
        {
          _method: "POST",
          name: "",
          permissions: [],
        },
        { key: "roleCreation" }
      ),
      selectedPermissions: [],
    };
  },

  mounted() {
    if (this.on) {
      setTimeout(() => {
        this.$refs.name.$refs.input.focus();
      }, 250);
    }

    this.resetPermissions();
  },

  methods: {
    resetPermissions() {
      this.selectedPermissions = [];

      for (var i = 0; i < this.permissions.data.length; i++) {
        this.selectedPermissions.push({
          permission: this.permissions.data[i].name,
          value: false,
          display_name: this.t(this.permissions.data[i].name),
        });
      }
    },

    storeRole() {
      this.form.permissions = this.activatedPermissions;

      this.form.post(this.route("roles.store"), {
        preserveScroll: true,
        onSuccess: (page) => {
          this.close();
        },
      });
    },

    close() {
      this.form.reset();
      this.resetPermissions();
      this.form.clearErrors();
      this.$emit("toggleVisibility");
    },
  },

  watch: {
    on: {
      handler: (on) => {
        if (on) {
          setTimeout(() => {
            this.$refs.name.$refs.input.focus();
          }, 250);
        }
      },
    },
  },
};
</script>


<i18n>
{
  "es": {
    "role_create": "Crear nuevo rol",
    "description": "Establece la información del rol y sus permisos",
    "name": "Nombre del rol",
    "permissions": "Permisos",
    "save": "Guardar",
    "cancel": "Cancelar",
    "manage staff": "Administrar personal y permisos",
    "manage customers": "Administrar clientes",
    "manage services": "Administrar servicios",
    "manage equipment": "Administrar equipos de medición",
  }
}
</i18n>
